import type { BuildType } from '../zod.js';
import type { WizardOptionCopy } from './types.js';

/**
 * Read-aloud copy for each Build Type. Used by the EngagementStep card grid
 * and the printable option-set leave-behind.
 */
export const BUILD_TYPE_COPY: Readonly<Record<BuildType, WizardOptionCopy>> = {
  'Fresh Build': {
    name: 'Fresh Build',
    oneLiner: 'Brand new website, no prior site to migrate from.',
    clientPitch:
      "We start from a blank page. No legacy site holds us back, no SEO history to preserve, no platform to fight with. We get to make every decision on purpose. This is the fastest path to something that feels like you.",
    bestFor: ['New businesses', 'Brand relaunches', 'Anyone with no current site', 'First-time clients'],
    avoidIf: ['You already rank well in Google for important terms', 'You have years of content to preserve'],
    feel: ['Open', 'Decisive', 'Quick'],
  },
  'Site Rescue': {
    name: 'Site Rescue',
    oneLiner: 'Replace a broken or abandoned site with something cleaner and faster.',
    clientPitch:
      "Your current site is hurting more than it helps - it's slow, broken, embarrassing, or stuck on a platform nobody can edit anymore. We replace it cleanly, keep the URLs that matter so Google doesn't punish you, and give you something you can be proud to send people to.",
    bestFor: ['Outdated WordPress sites', 'Abandoned Wix builds', 'Sites the developer ghosted', 'Anything that loads slow'],
    avoidIf: ['Your current site is genuinely working well', 'You only need a small content tweak'],
    feel: ['Relieving', 'Fixing', 'Modern'],
  },
  Redesign: {
    name: 'Redesign',
    oneLiner: 'Same content, sharper look. Keep your search ranking intact.',
    clientPitch:
      "Your site works, but it doesn't look or feel like the business you've become. We keep your structure and SEO equity, then redesign the surface so it matches your level today. People who already know you will see the upgrade. People meeting you for the first time will see who you are now.",
    bestFor: ['Businesses outgrowing their look', 'Brand evolutions', 'Pre-funding refreshes', 'Pre-acquisition cleanups'],
    avoidIf: ['You also need entirely different content', 'You are open to changing your URLs'],
    feel: ['Polished', 'Confident', 'Familiar'],
  },
  'Replatform Migration': {
    name: 'Replatform Migration',
    oneLiner: 'Move off Wix / Squarespace / WordPress onto a fast, owned stack.',
    clientPitch:
      "You're stuck on a platform that's expensive, slow, or limiting what you can do. We move you onto a modern stack you fully own - cleaner code, faster pages, lower hosting cost, and freedom to add anything custom later. We bring your content with us and redirect old URLs so search rankings carry over.",
    bestFor: ['Platform escapees', 'Cost-cutters', 'Custom-feature seekers', 'Performance-conscious brands'],
    avoidIf: ['You actively use platform-specific features that don\'t exist elsewhere', 'You have no in-house tech help and prefer simple drag-drop'],
    feel: ['Liberating', 'Engineered', 'Long-term'],
  },
  'Landing Page': {
    name: 'Landing Page',
    oneLiner: 'Single high-conversion page for one offer or campaign.',
    clientPitch:
      "Sometimes you don't need a whole website - you need one perfect page that closes the deal. Whether it's a launch, a campaign, or a paid-ads destination, we build a single sharp page focused on one outcome. Faster to ship, easier to test, easier to iterate.",
    bestFor: ['Product launches', 'Paid-ad destinations', 'Event registrations', 'Lead magnets'],
    avoidIf: ['You need multiple service pages', 'You want SEO across many keywords'],
    feel: ['Sharp', 'Focused', 'Convertible'],
  },
  'Subdomain / Sister Site': {
    name: 'Subdomain / Sister Site',
    oneLiner: 'A new property living under your existing brand.',
    clientPitch:
      "You already have a main site. This is a separate property that lives alongside it - a different audience, a different offer, a different mood, but still recognizably yours. Think `events.brand.com` or `pros.brand.com`. We reuse your visual DNA so the family resemblance is obvious without locking you into the same structure.",
    bestFor: ['Multi-brand parents', 'B2B/B2C splits', 'Spinoff offerings', 'Geographic micro-sites'],
    avoidIf: ['You really just need a new section on your existing site'],
    feel: ['Connected', 'Distinct', 'Strategic'],
  },
  'Demo / Spec Build': {
    name: 'Demo / Spec Build',
    oneLiner: 'Speculative pitch we ship before we have a signed deal.',
    clientPitch:
      "This is the version we build to win you over. We're showing you what we'd do if you said yes. It's free for you to look at, completely free of obligation, and we get to be opinionated because we're not designing by committee yet. If it lands, we make it real.",
    bestFor: ['Cold outreach', 'Big-account chases', 'Proof-of-concept pitches', 'Capability demos'],
    avoidIf: ['Client is already committed and ready to brief'],
    feel: ['Opinionated', 'Bold', 'Free-of-charge'],
  },
  'Internal / Personal': {
    name: 'Internal / Personal',
    oneLiner: 'Our own properties - portfolio, marketing, internal tools.',
    clientPitch:
      "Not for an external client - this is something I'm building for myself or my own business. Same standards apply, but I'm both the designer and the audience.",
    bestFor: ['Personal portfolio', 'Studio marketing site', 'Internal tools', 'Side projects'],
    avoidIf: ['You want this to look like client work for a pitch'],
    feel: ['Self-aware', 'Thoughtful', 'Crafted'],
  },
  'Event / Time-Boxed': {
    name: 'Event / Time-Boxed',
    oneLiner: 'A site with a finite lifespan - wedding, fundraiser, conference.',
    clientPitch:
      "This site exists to do one job by one date. After the event, it might come down or quietly archive itself. Because the timeline is real and the audience is specific, we can be more theatrical than usual - you're not optimizing for years of evergreen content, you're throwing a party.",
    bestFor: ['Weddings', 'Fundraisers', 'Conferences', 'One-night-only experiences'],
    avoidIf: ['You actually need an evergreen site that just happens to launch around an event'],
    feel: ['Theatrical', 'Time-bound', 'Memorable'],
  },
  'Whitelabel / Reseller': {
    name: 'Whitelabel / Reseller',
    oneLiner: 'Built for an agency partner who resells under their brand.',
    clientPitch:
      "Another agency or partner is reselling our work to their client under their own brand. We never appear on the deliverable. The constraints are tighter (no Nuzum signature labels, no \"built by\" credit) but the standards are the same.",
    bestFor: ['Agency partners', 'Whitelabel resellers', 'Subcontracted production'],
    avoidIf: ['The client is direct and would benefit from the Nuzum brand association'],
    feel: ['Discreet', 'Reliable', 'Behind-the-scenes'],
  },
};
